var MAX_SIGNED_INT64 = 9007199254740992
var MIN_SIGNED_INT64 = -MAX_SIGNED_INT64
var MAX_INT32 = 4294967295
var MAX_SIGNED_INT32 = 2147483647
var MAX_INT16 = 65535
var MAX_SIGNED_INT16 = 32767
var MAX_BYTE = 255
var MAX_SIGNED_BYTE = 127
var HI32_VALUE = 4294967296
var MAX_INT_64 = Math.pow(2, 53)
export enum ErrorCodes {
    INVALID_IO_ADAPTER,
    BUFFER_UNDERRUN,
    RECONNECT_FAILED,
    IO_ADAPTER_ERROR,
}


export class FirebaseException extends Error{
	name : string
	message : string
	code : ErrorCodes
	constructor (errorCode, errorMessage) {
		super(errorMessage)
		this.name = 'FirebaseException'
		this.message = errorMessage
		this.code = errorCode
	};
}

export interface ObjectFactory {
    create(classId:number, ba : ByteArray) : ProtocolObject;
    createClass(classId:number) : ProtocolObject;
    version() : number;
}
export interface ProtocolObjectConstructor<T extends ProtocolObject> {
    CLASSID: number;
    new(...args: any[]): T;
}
export interface ProtocolObject {
    classId() : number;
    save(ba : ByteArray) : void;
    load(ba : ByteArray) : void;
}
export interface ByteArray {
    remaining() : number;
    getBuffer() : number[];
    readBoolean() : boolean;
    checkBuffer(size : number) : void;
    readLong() : number;
    readInt() : number;
    readShort(): number;
    readByte() : number;
    readUnsignedInt() : number;
    readUnsignedShort() : number;
    readUnsignedByte() : number;
    readString() : string;
    writeBoolean(value : boolean) :  void;
    writeString(str : string) : void;
    twoCompliment(buffer : number[]) : void;
    writeLong(value : number) : void;
    writeInt(value : number) : void;
    writeShort(value : number) : void;
    writeByte(value : number) : void;
    writeUnsignedInt(value : number) : void;
    writeUnsignedShort(value : number) : void;
    writeUnsignedByte(value : number) : void;
    createServiceDataArray(classId : number) : number[];
    createDataArray() : number[];
    createGameDataArray(classId : number) : number[];
    writeArray(byteArray: number[] | ByteArray) : void;
    readArray(count : number) : number[];
    fromBase64String(input : string) : number[];
    toBase64String(input : number[]) : string;
}

export class ByteArrayImpl implements ByteArray{
	buffer : number[]
	position : number
	constructor(array?){
		this.buffer = []
		if (array) {
			this.buffer = array
		} else {
			this.buffer = []
		}
		this.position = 0
	}

	remaining() {
		return this.buffer.length - this.position
	}
	getBuffer() {
		return this.buffer
	}
	readBoolean() {
		this.checkBuffer(1)
		return this.readByte() !== 0
	}
	checkBuffer(size) {
		if (this.remaining() < size) {
			throw new FirebaseException(ErrorCodes.BUFFER_UNDERRUN, 'Buffer underrun')
		}
	}
	readLong() {
		this.checkBuffer(8)
		var negate = this.buffer[this.position] & 128, x = 0, carry = 1
		var i, v, m
		for (i = 7, m = 1;
			i >= 0;
			i--, m *= 256) {
			v = this.buffer[i + this.position]
			if (negate) {
				v = (v ^ 255) + carry
				carry = v >> 8
				v = v & 255
			}
			x += v * m
		}
		if (x > MAX_INT_64) {
			throw new RangeError('Precision lost when converting 64bit number')
		}
		this.position += 8
		return negate ? -x : x
	}
	readInt() {
		this.checkBuffer(4)
		var value = (this.buffer[this.position++] << 24)
		value += (this.buffer[this.position++] << 16)
		value += (this.buffer[this.position++] << 8)
		value += this.buffer[this.position++]
		if (value > MAX_SIGNED_INT32) {
			value -= MAX_INT32 + 1
		}
		return value
	}
	readShort() {
		this.checkBuffer(2)
		var value = (this.buffer[this.position++] << 8)
		value += this.buffer[this.position++]
		if (value > MAX_SIGNED_INT16) {
			value -= MAX_INT16 + 1
		}
		return value
	}
	readByte() {
		this.checkBuffer(1)
		var value = this.buffer[this.position++]
		if (value > MAX_SIGNED_BYTE) {
			value -= MAX_BYTE + 1
		}
		return value
	}
	readUnsignedInt() {
		var value = this.readInt()
		if (value < 0) {
			value += MAX_INT32 + 1
		}
		return value
	}
	readUnsignedShort() {
		var value = this.readShort()
		if (value < 0) {
			value += MAX_INT16 + 1
		}
		return value
	}
	readUnsignedByte() {
		var value = this.readByte()
		if (value < 0) {
			value += MAX_BYTE + 1
		}
		return value
	}
	readString() {
		this.checkBuffer(2)
		var length = this.readShort()
		var byteArray = this.buffer.slice(this.position, this.position + length)
		this.position += length
		return UTF8.fromByteArray(byteArray)
	}
	writeBoolean(value) {
		this.writeByte(value === true ? 1 : 0)
	}
	writeString(str : string) {
		var i
		var byteArray = UTF8.toByteArray(str)
		this.writeUnsignedShort(byteArray.length)
		for (i = 0;
			i < byteArray.length;
			i++) {
			this.writeUnsignedByte(byteArray[i])
		}


	}
	twoCompliment(buffer) {
		var carry = 1
		var v, i
		for (i = 7;
			i >= 0;
			i--) {
			v = (buffer[i] ^ 255) + carry
			buffer[i] = v & 255
			carry = v >> 8
		}
	}
	writeLong(value) {
		var negative
		var buffer = [0, 0, 0, 0, 0, 0, 0, 0]
		var lowPart
		var i
		if (value > MAX_SIGNED_INT64) {
			throw new RangeError('value to big for long')
		} else {
			if (value < MIN_SIGNED_INT64) {
				throw new RangeError('value to small for long')
			}
		}
		negative = value < 0
		value = Math.abs(value)
		lowPart = value % HI32_VALUE
		value = value / HI32_VALUE
		value = value | 0
		for (i = 7;
			i >= 0;
			i--) {
			buffer[i] = (lowPart & 255)
			lowPart = i === 4 ? value : lowPart >>> 8
		}
		if (negative) {
			this.twoCompliment(buffer)
		}
		for (i = 0;
			i < buffer.length;
			i++) {
			this.buffer.push(buffer[i])
		}
	}
	writeInt(value) {
		var byte1 = (value & 4278190080) >> 24
		if (byte1 < 0) {
			byte1 += 256
		}
		this.buffer.push(byte1)
		this.buffer.push((value & 16711680) >> 16)
		this.buffer.push((value & 65280) >> 8)
		this.buffer.push(value & 255)
	}
	writeShort(value) {
		this.buffer.push(((value & 65280) >> 8))
		this.buffer.push(value & 255)
	}
	writeByte(value) {
		this.buffer.push(value & 255)
	}
	writeUnsignedInt(value) {
		if (value < 0) {
			value += MAX_INT32
		}
		this.writeInt(value)
	}
	writeUnsignedShort(value) {
		value &= 65535
		if (value < 0) {
			value += MAX_INT16
		}
		this.writeShort(value)
	}
	writeUnsignedByte(value) {
		value &= 255
		if (value < 0) {
			value += MAX_BYTE
		}
		this.writeByte(value)
	}
	createServiceDataArray(classId) {
		return this.createGameDataArray(classId)
	}
	createDataArray() {
		var adata = new ByteArrayImpl()
		adata.writeUnsignedInt(this.buffer.length)
		var arrayToSend = adata.buffer.concat(this.buffer)
		return arrayToSend
	}
	createGameDataArray(classId) {
		var header = new ByteArrayImpl()
		header.writeUnsignedInt(this.buffer.length)
		header.writeUnsignedByte(classId)
		var arrayToSend = header.buffer.concat(this.buffer)
		return arrayToSend
	}
	writeArray(byteArray) {
		var source
		source = (byteArray instanceof Array && byteArray.length > 0) ? new ByteArrayImpl(byteArray) : byteArray
		var newBuffer = this.buffer.concat(source.buffer)
		this.buffer = newBuffer
	}
	readArray(count) {
		var i, len, element, target = []
		len = count || this.remaining()
		for (i = 0;
			i < len;
			i++) {
			element = this.readUnsignedByte()
			if (typeof element !== 'undefined') {
				target.push(element)
			}
		}
		return target
	}
	fromBase64String(input) {
		return ByteArrayImpl.fromBase64String(input)
	}
	static fromBase64String(input) {
		var result = []
		var _keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
		var chr1, chr2, chr3
		var enc1, enc2, enc3, enc4
		var i = 0
		input = input.replace(/[^A-Za-z0-9\+\/\=]/g, '')
		while (i < input.length) {
			enc1 = _keyStr.indexOf(input.charAt(i++))
			enc2 = _keyStr.indexOf(input.charAt(i++))
			enc3 = _keyStr.indexOf(input.charAt(i++))
			enc4 = _keyStr.indexOf(input.charAt(i++))
			chr1 = (enc1 << 2) | (enc2 >> 4)
			chr2 = ((enc2 & 15) << 4) | (enc3 >> 2)
			chr3 = ((enc3 & 3) << 6) | enc4
			result.push(chr1)
			if (enc3 !== 64) {
				result.push(chr2)
			}
			if (enc4 !== 64) {
				result.push(chr3)
			}
		}
		return result
	}

	toBase64String(input) {
		return ByteArrayImpl.toBase64String(input)
	}
	static toBase64String(input) {
		var output = ''
		var _keyStr = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
		var i = 0
		var current = 0
		var previous = 0
		var byteNum = 0
		while (i < input.length) {
			current = input[i] < 0 ? input[i] + 256 : input[i]
			byteNum = i % 3
			switch (byteNum) {
			case 0:
				output += _keyStr.charAt(current >> 2)
				break
			case 1:
				output += _keyStr.charAt((previous & 3) << 4 | (current >> 4))
				break
			case 2:
				output += _keyStr.charAt((previous & 15) << 2 | (current >> 6))
				output += _keyStr.charAt(current & 63)
				break
			}
			previous = current
			i++
		}
		if (byteNum === 0) {
			output += _keyStr.charAt((previous & 3) << 4)
			output += '=='
		} else {
			if (byteNum === 1) {
				output += _keyStr.charAt((previous & 15) << 2)
				output += '='
			}
		}
		return output
	};
}
export class UTF8 {
	static toByteArray(str : string) {
		var i, j
		var bytes = []
		for (i = 0; i < str.length; i++) {
			if (str.charCodeAt(i) <= 127) {
				bytes.push(str.charCodeAt(i))
			} else {
				var h = encodeURIComponent(str.charAt(i)).substr(1).split('%')
				for (j = 0;
					j < h.length;
					j++) {
					bytes.push(parseInt(h[j], 16))
				}
			}
		}
		return bytes



	}
	static fromByteArray(bytes : number[]) {
		var i
		var str = ''
		for (i = 0; i < bytes.length; i++) {
			if (bytes[i] <= 127) {
				if (bytes[i] === 37) {
					str += '%25'
				} else {
					str += String.fromCharCode(bytes[i])
				}
			} else {
				str += '%' + bytes[i].toString(16).toUpperCase()
			}
		}
		return decodeURIComponent(str)
	}
}
